<template>
  <section class="simulations-read">
    <information-accordion
      v-if="simulation.uuid"
      :sections="sections"
      class="simulations-read-accordion"
      collapsed
    >
      <template
        v-if="tagged"
        v-slot:[`${sections[0]._ID}-subtitle`]
      >
        <context-tag
          color="external"
          class="simulations-read-online"
          :icon="['fas', 'right-to-bracket']"
        >
          Via {{ tagged }}
        </context-tag>
      </template>

      <template v-slot:[`simulation-whatsapp-value`]>
        <form-button
          target="_blank"
          size="small"
          :icon="['fab', 'whatsapp']"
          :href="simulationSectionWhatsApp"
        >
          Entrar em contato
        </form-button>
      </template>
    </information-accordion>

    <form-slider
      v-if="simulation.uuid && !denied"
      :value="down"
      :min="min"
      :max="max"
      :marks="marks"
      :formatter="monetize"
      :disabled="processing"
      class="simulations-read-slider"
      label="Entrada"
      placeholder="R$ 0,00"
      @change="change"
    />

    <embed-frame
      class="simulations-read-frame"
      endpoint="simulations/read"
      placeholder="Carregando simulação..."
      :settings="embedSettings"
      :callbacks="embedCallbacks"
    />

    <!-- IMPORTANT: DO NOT WRAP THE CONTENT BELLOW (v-show ones) IN A TEMPLATE TAG. VUE BUG! -->

    <context-separator v-show="!processing" />

    <form-button-group
      v-show="!processing"
      align="right"
      class="simulations-read-buttons"
      mobile
    >
      <form-button
        v-if="controlable"
        color="secondary"
        :href="control"
      >
        Editar controle comercial
      </form-button>

      <form-button
        :href="retry"
      >
        Alterar dados da simulação
      </form-button>
    </form-button-group>
  </section>
</template>

<script>
  import { mapState } from 'vuex';

  import origin from '@sdk/helpers/interface/origin';

  import items from '@sdk/helpers/misc/items';
  import { createdAt } from '@sdk/helpers/formatters/momentize';
  import monetize from '@sdk/helpers/formatters/monetize';
  import namelize from '@sdk/helpers/formatters/namelize';
  import dateize from '@sdk/helpers/formatters/dateize';
  import patternize from '@sdk/helpers/formatters/patternize';

  import {
    DENIED,
    NEUTRAL,
    APPROVED,
    IDENTIFIERS,
  } from '@sdk/views/Simulations/Read/status';

  import { CELLPHONE_FORMAT } from '@cordel/helpers/form/validators/cellphone';
  import { CPF_FORMAT } from '@cordel/helpers/form/validators/cpf';
  import { CNPJ_FORMAT } from '@cordel/helpers/form/validators/cnpj';

  import ContextSeparator from '@cordel/components/Context/Separator.vue';

  import FormSlider from '@cordel/components/Form/Slider.vue';
  import FormButton from '@cordel/components/Form/Button.vue';
  import FormButtonGroup from '@cordel/components/Form/Button/Group.vue';

  import EmbedFrame from '@sdk/views/Embed/Frame.vue';

  import payload from '@sdk/helpers/payload/simulation';

  import ContextTag from '@cordel/components/Context/Tag.vue';

  import InformationAccordion from '@cordel/components/Information/Accordion.vue';

  const BUTTON_ID = 'home-help-button';

  const EMPTY = ['original_condition', '', null, undefined];
  const LIMIT = 99;

  export default {
    name: 'SimulationsRead',
    components: {
      ContextSeparator,

      FormButton,
      FormButtonGroup,
      FormSlider,

      EmbedFrame,

      ContextTag,

      InformationAccordion,
    },
    data() {
      return {
        simulation: {},
        processing: true,
        monetize,
        controlable: true,
      };
    },
    computed: {
      ...mapState([
        'simulations',
        'auth',
      ]),

      down() {
        const condition = (this.simulation.conditions || [])
          .find(c => EMPTY.includes(c.suggestion_reason));

        if (condition) {
          return condition.down_payment;
        }

        return 0;
      },

      min() {
        return 0;
      },
      max() {
        return parseInt(this.simulation.assets_value * LIMIT / 100, 10);
      },
      marks() {
        if (!this.simulation.assets_value) {
          return {};
        }

        const marks = { 0: '0%' };

        for (let i = 1; (i * 10) < LIMIT; i += 1) {
          marks[parseInt(this.simulation.assets_value * i * 10 / 100, 10)] = String(i * 10);
        }

        if (!marks[this.max]) {
          marks[this.max] = `${LIMIT}%`;
        }

        return marks;
      },

      retry() {
        let name = 'simulations-new';

        const {
          store_id: storeID,
          proposal_id: proposalID,
          proposal_attempt_id: attemptID,
        } = this.$route.query;

        const {
          uuid: simulationUUID,
        } = this.$route.params;

        const params = {};
        const query = { store_id: storeID };

        if (proposalID) {
          query.integrated = true;

          params.proposal_id = proposalID;
          params.proposal_attempt_id = attemptID;

          name = attemptID ? 'proposal-attempts-edit' : 'proposal-attempts-new';
        } else {
          query.simulation_uuid = simulationUUID;
        }

        return {
          name,
          query,
          params,
        };
      },
      control() {
        return {
          ...this.retry,
          query: {
            ...this.retry.query,

            commercial_control: true,
          },
        };
      },

      simulationSection() {
        if (
          !this.simulation
          || !this.simulation.lead
          || !this.simulation.vehicle
          || !this.simulation.items
        ) {
          return {};
        }

        const {
          proposal_simulation: proposalSimulation,
        } = this.simulation;

        let storeName = this.simulation.store && this.simulation.store.name;

        if (!storeName && proposalSimulation && proposalSimulation.store) {
          storeName = proposalSimulation.store.name;
        }

        let sellerName = this.simulation.seller && this.simulation.seller.name;

        if (!sellerName && proposalSimulation && proposalSimulation.seller) {
          sellerName = proposalSimulation.seller.name;
        }

        const documentationValue = items(this.simulation, 'documentation');
        const accessoryValue = items(this.simulation, 'accessory');

        const lead = [{
          label: this.simulation.lead.cpf_cnpj.length > CPF_FORMAT.length ? 'CNPJ' : 'CPF',
          value: patternize(this.simulation.lead.cpf_cnpj, {
            format: CPF_FORMAT,
            bigFormat: CNPJ_FORMAT,
          }),
          group: 'scope',
        }, {
          label: 'Celular',
          value: this.simulationSectionPhone,
          group: 'scope',
        }, {
          _ID: 'whatsapp',
          label: 'WhatsApp',
          value: this.simulationSectionPhone,
          group: 'scope',
        }, {
          label: 'Nascimento',
          value: dateize(this.simulation.lead.birthdate),
          group: 'scope',
        }, {
          label: 'Possui carteira de motorista?',
          value: this.simulation.lead.has_cnh,
          group: 'scope',
        }, {
          label: 'Email',
          value: this.simulation.lead.email,
          group: 'scope',
        }, {
          label: 'Gênero',
          value: this.simulation.lead.gender && this.simulation.lead.gender.label,
          group: 'scope',
        }, {
          label: 'Ocupação',
          value: this.simulation.lead.occupation && this.simulation.lead.occupation.label,
          group: 'scope',
        }, {
          label: 'Renda',
          value: this.simulation.lead.monthly_income
            ? monetize(this.simulation.lead.monthly_income)
            : undefined,
          group: 'scope',
        }, {
          label: 'CEP',
          value: this.simulation.lead.address && this.simulation.lead.address.zip_code,
          group: 'scope',
        }];

        const vehicle = [{
          label: 'Veículo',
          value: `${this.simulation.vehicle.vehicle_model.brand} ${this.simulation.vehicle.vehicle_model.model_name}`,
        }, {
          label: 'Modelo',
          value: this.simulation.vehicle.vehicle_model.version,
        }];

        if (this.simulation.vehicle.license_plate_code) {
          vehicle.push({
            label: 'Placa',
            value: this.simulation.vehicle.license_plate_code,
          });
        }

        vehicle.push({
          label: 'Veículo novo ou usado?',
          value: this.simulation.vehicle.zero_km ? 'Novo' : 'Usado',
        }, {
          label: 'Ano de fabricação e modelo',
          value: `${this.simulation.vehicle.manufacture_year}/${this.simulation.vehicle.model_year}`,
        }, {
          label: 'Valor do veículo',
          value: monetize(this.simulation.assets_value || 0),
        }, {
          label: 'Valor da documentação',
          value: monetize(documentationValue),
        }, {
          label: 'Valor de acessório',
          value: monetize(accessoryValue),
        });

        return [{
          _ID: 'simulation',
          _COLLAPSED: true,
          title: namelize(this.simulation.lead && this.simulation.lead.name) || 'Desconhecido',
          subtitle: `cadastrada dia ${createdAt(this.simulation.created_at)}`,
          hierarchy: 'h3',
          fields: [...lead, {
            label: 'Loja',
            value: (this.simulation.store && this.simulation.store.name) || 'Desconhecida',
            group: 'owners',
          }, {
            label: 'Vendedor',
            value: namelize(this.simulation.seller && this.simulation.seller.name) || 'Desconhecido',
            group: 'owners',
          }, ...vehicle],
        }];
      },
      simulationSectionPhone() {
        if (!this.simulation.lead.phone_number) return null;

        return patternize(this.simulation.lead.phone_number.replace('+55', ''), {
          format: CELLPHONE_FORMAT,
        });
      },
      simulationSectionWhatsApp() {
        if (!this.simulation.lead.phone_number) return null;

        return `https://wa.me/${this.simulation.lead.phone_number.replace('+', '')}`;
      },
      sections() {
        return (this.simulation && this.simulation.uuid) ? this.simulationSection : [];
      },

      embedSettings() {
        const {
          proposal_id: proposalID,
          id: proposalAttemptID,
        } = this.$route.params;

        const {
          condition_ids: conditionIDs,
          focus_condition_id: conditionID,
        } = this.$route.query;

        const settings = {
          // The re-simulations this screen can fire (the embed's Down.vue, which
          // reads `simulations.form`) go out with the same origin as the form host
          // next door - see the comment in Simulations/Form.vue for why a host that
          // declares nothing is read as an external plugin lead.
          simulations: {
            form: {
              payload: { origin: 'pwa' },
            },
            read: {
              _enable_support: true,
              _enable_best_condition_return: true,
              _enable_non_integrated_proposal_attempt: true,
              _enable_negative_on_best_conditions: true,
              _enable_commercial_control_unallowed_tags: true,
              _enable_multiple_simulations_on_customers_form_action: true,

              _disable_bank_ranking: [true, 'true'].includes(this.$route.query.has_external_proposal),

              _condition_ids: conditionIDs && conditionIDs.split(','),
              _focus_condition_id: conditionID,

              store_id: this.$route.query.store_id,
              uuid: this.$route.params.uuid,
              related: this.$route.query.related,
            },
          },
        };

        if (proposalID) {
          // eslint-disable-next-line no-underscore-dangle
          settings.simulations.read._proposal = { id: proposalID };

          if (proposalAttemptID) {
            // eslint-disable-next-line no-underscore-dangle
            settings.simulations.read._proposal.proposal_attempt = { id: proposalAttemptID };
          }
        }

        return settings;
      },
      embedCallbacks() {
        return {
          'simulation:read': data => this.callback(data, false),
          'simulation:read:processing': data => this.callback(data, true),
          'simulation:read:action': this.action,

          'store:read': this.store,
        };
      },

      denied() {
        const conditions = (this.simulation && this.simulation.conditions) || [];

        const approved = conditions.some(c => true
          && c.success
          && c.available
          && [IDENTIFIERS[NEUTRAL], IDENTIFIERS[APPROVED], null].includes(c.pre_approval_status));

        const denied = conditions.some(c => true
          && c.success
          && c.pre_approval_status === IDENTIFIERS[DENIED]);

        return !approved && denied;
      },

      tagged() {
        return origin(this.simulation.proposal_simulation) || origin(this.simulation);
      },
    },
    methods: {
      callback(data, loading) {
        const {
          query,
          params,
        } = this.$route;

        this.processing = loading;
        this.simulation = data.data;

        if (!/-/.test(params.uuid)) {
          this.$router.replace({
            name: 'simulations-read',
            params: {
              uuid: this.simulation.uuid,
            },
            query: {
              ...query,
            },
          });
        }
      },
      action(data) {
        if (['customers/form', 'customers/force'].includes(data.action)) {
          const query = {
            store_id: data.settings.store_id,

            cpf: data.settings.cpf,

            proposal_id: this.$route.query.proposal_id,
            proposal_attempt_id: this.$route.query.proposal_attempt_id,

            condition_ids: (data.settings.condition_ids || []).join(','),
          };

          if (data.settings.simulation_uuids) {
            query.simulation_uuids = data.settings.simulation_uuids.join(',');
          } else {
            query.simulation_uuid = data.settings.simulation_uuid;
          }

          this.$router.push({
            name: 'customers-new',
            query,
          });
        }

        if (['simulations/append'].includes(data.action)) {
          const to = window.location.href.replace(/&condition_ids=([\d,]+)/, '');
          window.history.pushState(null, '', `${to}&related=${data.settings.simulation_uuid}`);
        }

        if (data.action === 'misc/support') {
          const button = document.getElementById(BUTTON_ID);

          if (button) {
            button.click();

            return;
          }

          this.$store.dispatch('toasts/create', {
            color: 'warning',
            message: 'Por favor, clique no botão de suporte no lado direito desta página.',
          });
        }
      },

      change(value) {
        this.create(value);
      },

      create(value) {
        this.$store
          .dispatch('simulations/create', payload(this.simulation, value))
          .then(this.created)
          .catch(this.failed);
      },
      created() {
        const { uuid } = this.simulations.create.item;

        this.$router.push({
          name: 'simulations-read',
          params: {
            uuid,
          },
          query: {
            ...this.$route.query,
          },
        });
      },
      failed() {
        this.$store.dispatch('simulations/clear', { action: 'read' });
      },
      store(data) {
        this.controlable = this.auth.user.role.identifier !== 'salesman'
          || data.store.allow_commercial_control_customization_by_sellers;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .simulations-read {
    padding: 0 var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: 0 var(--product--pwa--horizontal);
    }
  }

  .simulations-read-accordion {
    margin-bottom: var(--cordel-legacy-spacing-blaster);
  }

  .simulations-read-frame {
    &:first-child {
      margin-top: 0;
    }

    margin-top: var(--cordel-legacy-spacing-blaster);
  }
</style>
